import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import * as ReactGA from 'react-ga';
import { BOTTOM_PADDING, HEADER_HEIGHT, SORT_TYPES } from 'common/constants';
import { createRoute } from 'routes/patientRouteUtils';
import { PAGE_LIMIT } from 'state/services/searchApiUtils';
import {
  updateCurrentPage,
  updateCurrentSort,
} from 'state/reducers/search/searchActions';
import { SEARCH_REDUCERS } from 'state/reducers/search/searchReducers';
import { updateSelectedTab } from 'state/reducers/patient/patientActions';
import { checkIdtfFeatureEnabled } from 'configs';
import { PATIENT_FILE_TABS } from '../../pages/PatientFile/PatientFileUtils';
import TableHeader from '../../components/Table/TableHeader';
import {
  PATIENT_TABLE_COLUMN_HEADERS,
  getNextSortDirection,
} from '../../components/Table/utils';
import Paginator from '../../components/Table/Paginator';
import SearchResultsTable from '../../components/Search/SearchResultsTable';
import SearchResultsHeader from '../../components/Search/SearchResultsHeader';

import styles from './PatientSearchResults.css';

class PatientSearchResults extends Component {
  constructor(props) {
    super(props);

    this.handleRowsScroll = this.handleRowsScroll.bind(this);
    this.handlePageChange = this.handlePageChange.bind(this);
  }

  state = {
    innerHeight: window.innerHeight,
    perPage: PAGE_LIMIT,
    scrollToIndex: 0,
    scrollPage: 1,
    // eslint-disable-next-line react/no-unused-state
    prevStopIndex: undefined, // used for debugging
    sortBy: PATIENT_TABLE_COLUMN_HEADERS.PATIENT,
    sortDirection: SORT_TYPES.ASC,
  };

  componentDidUpdate(prevProps) {
    const { patientSearchBarFields } = this.props;
    if (
      patientSearchBarFields !== prevProps.patientSearchBarFields &&
      patientSearchBarFields.searchParams.page === 1
    ) {
      this.resetScrollIndex();
    }
  }

  onRowClick = ({ rowData }) => {
    const { dispatch, history, patientSearchResults, me } = this.props;
    // console.log('this.props>>', rowData.connectionTemplateID);
    dispatch(updateSelectedTab(PATIENT_FILE_TABS.EKGS));
    if (checkIdtfFeatureEnabled(me)) {
      history.push(
        createRoute({
          patientId: rowData.id,
          kardiaRxEcg: 'patientSearch',
          idtf_connection_id: rowData.idtf_connection_id,
        }),
      );
    } else {
      history.push(
        createRoute({
          patientId: rowData.id,
          referral: 'patientSearch',
        }),
      );
    }
    try {
      ReactGA.event({
        category: 'SearchResults',
        action: 'Select-Row-Item',
        label: patientSearchResults.totalRecords.toString(),
      });
    } catch (err) {
      // silently fail
    }
  };

  headerRenderer = headerProps => {
    const { label, dataKey } = headerProps;
    const { sortBy, sortDirection } = this.state;
    const enableSort = true;

    return (
      <TableHeader
        label={label}
        columnHeader={dataKey}
        handleOnSortClick={this.handleOnSortClick}
        sortBy={sortBy}
        sortDirection={sortDirection}
        enableSort={enableSort}
      />
    );
  };

  handleOnSortClick = dataKey => {
    const { sortBy, sortDirection } = this.state;
    const { dispatch } = this.props;
    const nextSortDirection =
      sortBy === dataKey
        ? getNextSortDirection(sortDirection)
        : getNextSortDirection();

    const updatedSortParams = {
      sortBy: dataKey,
      sortDirection: nextSortDirection,
    };

    this.setState(
      {
        ...updatedSortParams,
      },
      () => {
        dispatch(updateCurrentSort(updatedSortParams));
        dispatch(updateCurrentPage(1));
        this.resetScrollIndex();
        try {
          ReactGA.event({
            category: 'SearchResults',
            action: 'Sort-Column',
            label: `${updatedSortParams.sortBy}-${updatedSortParams.sortDirection}`,
          });
        } catch (err) {
          // silently fail
        }
      },
    );
  };

  resetScrollIndex = () => {
    this.setState({
      scrollToIndex: 0,
    });
  };

  /**
   * handle pagination events by the user.
   * @param page - The page number to change to.
   * @param action - what action triggered the page change event.
   */
  handlePageChange(page, action = 'click') {
    const { dispatch } = this.props;
    this.setState(
      prevState => {
        const scrollToIndex = (page - 1) * prevState.perPage; // 25
        // console.log(scrollToIndex);
        return { scrollToIndex };
      },
      () => {
        dispatch(updateCurrentPage(page));
        try {
          ReactGA.event({
            category: 'SearchResults',
            action: `Pagination-Change-${action}`,
            label: page.toString(),
          });
        } catch (err) {
          // silently fail
        }
      },
    );
  }

  handleRowsScroll({ stopIndex }) {
    const { handlePageChange } = this;
    const { patientSearchResults } = this.props;
    const rowScrollIndex = stopIndex + 1;
    const { perPage } = this.state;
    const totalPageCount = patientSearchResults.totalRecords
      ? Math.ceil(patientSearchResults.totalRecords / perPage)
      : 0;
    const lastFetchedPage = Object.keys(patientSearchResults.pages).length;
    const currentPage = Math.ceil(rowScrollIndex / perPage);
    this.setState(
      {
        prevStopIndex: rowScrollIndex,
        scrollToIndex: undefined,
        scrollPage: currentPage === 0 ? 1 : currentPage,
      },
      () => {
        if (
          currentPage < totalPageCount && // are we at last page?
          currentPage >= lastFetchedPage &&
          rowScrollIndex === currentPage * perPage // is at bottom of table?
        ) {
          if (!patientSearchResults.isFetching) {
            const nextPage = currentPage + 1;
            handlePageChange(nextPage, 'scroll');
            try {
              ReactGA.event({
                category: 'SearchResults',
                action: 'Pagination-Scroll',
                label: nextPage.toString(),
              });
            } catch (err) {
              // silently fail
            }
          }
        }
      },
    );
  }

  render() {
    const {
      innerHeight,
      scrollToIndex,
      scrollPage,
      perPage,
      sortBy,
      sortDirection,
    } = this.state;
    const { patientSearchResults, patientSearchCurrentPage, me } = this.props;
    const {
      results,
      isFetching,
      error,
      totalRecords,
      totalPagesFetched,
    } = patientSearchResults;

    let searchResults = results;
    if (error || !results) {
      searchResults = [];
    }

    const resultsLength = searchResults.length;
    const contentHeight = innerHeight - HEADER_HEIGHT - 200 - BOTTOM_PADDING;
    const pageTotal =
      resultsLength < perPage ? resultsLength : totalPagesFetched * perPage;

    let noDataText = '';
    if (patientSearchResults.searchPerformed) {
      noDataText = 'No patients found.';
    }

    return (
      <div
        className={styles.patientSearchResults}
        style={{
          height: contentHeight,
        }}
      >
        <div className={styles.patientSearchResultsHeader}>
          <SearchResultsHeader
            isLoading={isFetching}
            pageTotal={pageTotal}
            resultsLength={resultsLength}
            totalRecords={totalRecords}
            className={styles.patientSearchResultsTotal}
          />
          <Paginator
            isLoading={isFetching}
            currentPage={scrollPage}
            perPage={perPage}
            totalRecords={totalRecords}
            onPageChange={this.handlePageChange}
          />
        </div>
        <SearchResultsTable
          styles={styles}
          isLoading={isFetching}
          noDataText={noDataText}
          onRowClick={this.onRowClick}
          error={error}
          rows={error ? [] : results}
          sortBy={sortBy}
          sortDirection={sortDirection}
          headerRenderer={this.headerRenderer}
          onRowsRendered={this.handleRowsScroll}
          scrollToIndex={scrollToIndex}
          loadingPage={patientSearchCurrentPage}
          me={me}
        />
      </div>
    );
  }
}

const mapStateToProps = ({ search }) => ({
  patientSearchResults: search[SEARCH_REDUCERS.INFINITE_SCROLL_REDUCER],
  patientSearchBarFields: search[SEARCH_REDUCERS.SEARCH_BAR_FIELDS],
  patientSearchCurrentPage: search[SEARCH_REDUCERS.SEARCH_CURRENT_PAGE],
});
export default withRouter(connect(mapStateToProps)(PatientSearchResults));



// WEBPACK FOOTER //
// ./src/views/containers/Search/PatientSearchResults.jsx